/**
 * The pool step — drag-ordered LANES (the order IS the rotation; a row is one
 * gateway plus its own sender IDs and AI prompt, Phase 40), an add-picker
 * over every route of the country (a route already in the pool gets a 2nd
 * lane), and the refusal when two lanes are the same thing twice.
 * Each row owns its sender controls and grows on click.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { closestCenter, DndContext, type DragEndEvent } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { AlertCircle } from 'lucide-react';
import { lanesProblems } from '@trafficpulse/shared';

import { SearchableSelect } from '@/components/ui';
import { dragEndToMove, useBinomDragSensors } from '@/features/flow-optimizer/tabs/standard/binom/workspace/list/use-binom-drag-sensors';
import type { Gateway } from '@/hooks/smswiser/core/useSMSWiser';

import type { DraftGateway } from '../../../../drop-generator.types';
import type { GatewayOp } from '../../state/gateway-draft';
import { laneAddOptions } from './pool-add-options';
import { PoolRow } from './PoolRow';

interface Props {
  countryKey?: string;
  gateway: DraftGateway;
  routes: Gateway[];
  onOp: (op: GatewayOp) => void;
}

export const PoolStep: FC<Props> = ({ gateway, routes, onOp, countryKey }) => {
  const sensors = useBinomDragSensors();
  const names = new Map(routes.map((r) => [r.id, r.name]));
  const nameOf = (id: number) => names.get(id) ?? `route #${id}`;
  const problems = lanesProblems(gateway.pool, (id) => names.get(id) ?? null);
  const showWeight = gateway.weightsMode === 'MANUAL' && (gateway.strategy === 'RANDOM' || gateway.strategy === 'SPLIT');

  const handleDragEnd = (event: DragEndEvent) => {
    const move = dragEndToMove(
      event,
      gateway.pool.map((r) => r.id),
    );
    if (move) onOp({ kind: 'poolMove', from: move.from, to: move.to });
  };

  return (
    <div className="flex flex-col gap-2">
      {gateway.pool.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-4 py-5 text-sm text-muted-foreground">
          No gateways yet — drops plan without a route (the timeline shows &ldquo;—&rdquo;). Add the routes this container may send through.
        </p>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={gateway.pool.map((r) => r.id)} strategy={verticalListSortingStrategy}>
            <div className="flex flex-col gap-1.5">
              {gateway.pool.map((row, index) => (
                <PoolRow countryKey={countryKey} key={row.id} row={row} index={index} name={nameOf(row.routeId)} defaults={gateway.senderDefaults} showWeight={showWeight} onOp={onOp} />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}
      {problems.length > 0 ? (
        <ul role="alert" className="flex flex-col gap-1 text-xs text-destructive duration-200 animate-in fade-in-0 motion-reduce:animate-none" data-testid="pool-problems">
          {problems.map((p) => (
            <li key={p} className="flex items-start gap-1.5">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
              {p}
            </li>
          ))}
        </ul>
      ) : null}
      <div className="flex flex-wrap items-center gap-3">
        {routes.length > 0 ? (
          <div className="w-96 max-w-full">
            <SearchableSelect options={laneAddOptions(routes, gateway.pool)} value="" onChange={(id) => onOp({ kind: 'poolAdd', routeId: Number(id) })} placeholder="+ Add a gateway…" searchPlaceholder="Search routes…" />
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">This country has no routes in the catalog.</span>
        )}
      </div>
    </div>
  );
};
